
import { useState, useEffect } from 'react'
import { lumi } from '../../lib/lumi'

interface Comment {
  _id: string
  userName: string
  content: string
  createdAt: string
  updatedAt?: string
}

export default function InteractiveSection() {
  const [likeCount, setLikeCount] = useState(0)
  const [hasLiked, setHasLiked] = useState(false)
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [userName, setUserName] = useState('')
  const [showCommentForm, setShowCommentForm] = useState(false)
  const [loading, setLoading] = useState(false)

  // 生成用户指纹
  const getUserFingerprint = () => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('Browser fingerprint', 2, 2)
    }
    const fingerprint = canvas.toDataURL() + navigator.userAgent + screen.width + screen.height
    return btoa(fingerprint).slice(0, 32)
  }

  // 获取点赞和评论数据
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 获取点赞数据
        const { list: likesList } = await lumi.entities.likes.list()
        setLikeCount(likesList.length)
        
        // // 检查当前用户是否已点赞
        // const userFingerprint = getUserFingerprint()
        // const userLike = likesList.find((like: any) => like.userFingerprint === userFingerprint)
        // setHasLiked(!!userLike)

        // 获取评论数据
        const { list: commentsList } = await lumi.entities.comments.list({
          sort: { createdAt: -1 }
        })
        setComments(commentsList.map((entity: any) => ({
          _id: entity._id,
          userName: entity.userName,
          content: entity.content,
          createdAt: entity.createdAt,
          updatedAt: entity.updatedAt
        })))
      } catch (error) {
        console.error('获取数据失败:', error)
      }
    }

    fetchData()
  }, [])

  // 处理点赞
  const handleLike = async () => {
    if (hasLiked) return

    try {
      const userFingerprint = getUserFingerprint()
      
      // 创建点赞记录
      await lumi.entities.likes.create({
        userFingerprint,
        createdAt: new Date().toISOString(),
        creator: 'anonymous'
      })

      setHasLiked(true)
      setLikeCount(prev => prev + 1)
    } catch (error) {
      console.error('点赞失败:', error)
    }
  }

  // 处理评论提交
  const handleCommentSubmit = async () => {
    if (!newComment.trim() || !userName.trim()) return

    setLoading(true)
    try {
      const userFingerprint = getUserFingerprint()
      
      await lumi.entities.comments.create({
        userName: userName.trim(),
        content: newComment.trim(),
        userFingerprint,
        createdAt: new Date().toISOString(),
        creator: 'anonymous'
      })

      // 重新获取评论列表
      const { list: commentsList } = await lumi.entities.comments.list({
        sort: { createdAt: -1 }
      })
      setComments(commentsList.map((entity: any) => ({
        _id: entity._id,
        userName: entity.userName,
        content: entity.content,
        createdAt: entity.createdAt,
        updatedAt: entity.updatedAt
      })))

      setNewComment('')
      setUserName('')
      setShowCommentForm(false)
    } catch (error) {
      console.error('评论提交失败:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mb-8">
      <div className="px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* 点赞区域 */}
          <div className="text-right mb-12">
            <button
              onClick={handleLike}
              disabled={hasLiked}
              className={`px-8 py-4 text-2xl font-bold border-4 border-black transition-none ${
                hasLiked 
                  ? 'bg-black text-white cursor-not-allowed' 
                  : 'bg-white text-black hover:bg-black hover:text-white'
              }`}
            >
              {hasLiked ? 'Liked' : 'Like'} {likeCount}
            </button>
          </div>

          {/* 评论区域 */}
          <div className="border-t-4 border-black pt-8">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-3xl font-bold">感谢大家的阅读～</h3>
              <button
                onClick={() => setShowCommentForm(!showCommentForm)}
                className="bg-black text-white px-6 py-3 text-lg font-bold hover:bg-white hover:text-black border-2 border-black transition-none"
              >
                {showCommentForm ? '取消' : '留言'}
              </button>
            </div>

            {/* 评论表单 */}
            {showCommentForm && (
              <div className="border-4 border-black p-6 mb-8 bg-white">
                <div className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="你的昵称"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      className="w-full p-3 border-2 border-black text-lg font-mono focus:outline-none focus:border-black"
                    />
                  </div>
                  <div>
                    <textarea
                      placeholder="写下你的留言..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={4}
                      className="w-full p-3 border-2 border-black text-lg font-mono resize-none focus:outline-none focus:border-black"
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={handleCommentSubmit}
                      disabled={loading || !newComment.trim() || !userName.trim()}
                      className="bg-black text-white px-8 py-3 text-lg font-bold hover:bg-white hover:text-black border-2 border-black transition-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? '发送中...' : '发送'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 评论列表 */}
            <div className="space-y-6 max-h-96 overflow-y-auto">
              {comments.length === 0 ? (
                <div className="text-center text-gray-600 py-8">
                  还没有留言，来写第一条吧～
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment._id} className="border-l-0 border-black pl-0">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-bold text-lg">{comment.userName}</span>
                      <span className="text-sm text-gray-600">
                        {new Date(comment.createdAt).toLocaleString('zh-CN')}
                      </span>
                    </div>
                    <div className="text-lg leading-relaxed">{comment.content}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
